import { useCallback } from "react";
import { syncResponseToBoardState, boardStateToSyncPayload } from "../persistence/serializer";
import { saveDraft, loadDraft, clearDraft, DRAFT_RIG_ID } from "../persistence/localStorage";

export default function useRigManagement({
  apiClient,
  currentRigId,
  setCurrentRigId,
  rigs,
  setRigs,
  rigNameRef,
  stateRef,
  loadState,
  saveNow,
  maxSavedRigs,
  trackEvent,
}) {
  const handleSwitchRig = useCallback(
    async (rigId) => {
      if (!apiClient || rigId === currentRigId) return;

      // Save draft state before switching away
      if (currentRigId === DRAFT_RIG_ID && stateRef) {
        saveDraft({
          nodes: stateRef.current.nodes,
          edges: stateRef.current.edges,
          rigCables: stateRef.current.rigCables,
          rigName: rigNameRef.current,
        });
      }

      if (rigId === DRAFT_RIG_ID) {
        const data = loadDraft();
        setCurrentRigId(DRAFT_RIG_ID);
        rigNameRef.current = data?.rigName || "Draft";
        loadState(data ? { nodes: data.nodes, edges: data.edges, rigCables: data.rigCables || [] } : { nodes: [], edges: [] });
        return;
      }

      try {
        const detail = await apiClient.getRig(rigId);
        setCurrentRigId(detail.id);
        rigNameRef.current = detail.name;
        setRigs((prev) =>
          prev.map((r) => (r.id === detail.id ? { ...r, description: detail.description, notes: detail.notes } : r))
        );
        const state = syncResponseToBoardState(detail);
        loadState(state);
      } catch {
        // ignore
      }
    },
    [apiClient, currentRigId, setCurrentRigId, setRigs, rigNameRef, stateRef, loadState]
  );

  const handleNewRig = useCallback(async () => {
    if (!apiClient) return;

    const activeCount = rigs.filter((r) => !r.over_limit && !r.draft).length;
    const hasDraft = rigs.some((r) => r.draft);

    if (activeCount >= maxSavedRigs) {
      if (hasDraft) return;

      // Save current draft state before creating a fresh draft
      if (currentRigId === DRAFT_RIG_ID && stateRef) {
        saveDraft({
          nodes: stateRef.current.nodes,
          edges: stateRef.current.edges,
          rigCables: stateRef.current.rigCables,
          rigName: rigNameRef.current,
        });
      }

      const draftRig = { id: DRAFT_RIG_ID, name: "Draft", draft: true };
      clearDraft();
      setRigs((prev) => [draftRig, ...prev.filter((r) => !r.draft)]);
      setCurrentRigId(DRAFT_RIG_ID);
      rigNameRef.current = "Draft";
      loadState({ nodes: [], edges: [] });
      return;
    }

    try {
      const newRig = await apiClient.createRig("New Rig");
      setRigs((prev) => [newRig, ...prev]);
      setCurrentRigId(newRig.id);
      rigNameRef.current = newRig.name;
      loadState({ nodes: [], edges: [] });
    } catch {
      // ignore
    }
  }, [apiClient, rigs, maxSavedRigs, currentRigId, stateRef, setRigs, setCurrentRigId, rigNameRef, loadState]);

  const handleDuplicateRig = useCallback(
    async (rigId) => {
      if (!apiClient) return;
      try {
        const result = await apiClient.duplicateRig(rigId);
        const newRigDetail = result.rig;

        setRigs((prev) => [{ id: newRigDetail.id, name: newRigDetail.name, slug: newRigDetail.slug, description: newRigDetail.description, notes: newRigDetail.notes, private: newRigDetail.private }, ...prev]);
        setCurrentRigId(newRigDetail.id);
        rigNameRef.current = newRigDetail.name;

        const state = syncResponseToBoardState(newRigDetail);
        loadState(state);

        trackEvent("rig_duplicated", {
          source_rig_id: rigId,
          new_rig_id: newRigDetail.id,
        });
      } catch {
        // ignore
      }
    },
    [apiClient, setRigs, setCurrentRigId, rigNameRef, loadState, trackEvent]
  );

  const handleTogglePrivate = useCallback(
    async (rigId, isPrivate) => {
      if (!apiClient) return;
      try {
        await apiClient.updateRig(rigId, { private: isPrivate });
        setRigs((prev) =>
          prev.map((r) => (r.id === rigId ? { ...r, private: isPrivate } : r))
        );
      } catch {
        // ignore
      }
    },
    [apiClient, setRigs]
  );

  const handleUpdateSlug = useCallback(
    async (rigId, slug) => {
      if (!apiClient) return;
      try {
        await apiClient.updateRig(rigId, { slug });
        setRigs((prev) =>
          prev.map((r) => (r.id === rigId ? { ...r, slug } : r))
        );
      } catch {
        // ignore
      }
    },
    [apiClient, setRigs]
  );

  const handleReorderRigs = useCallback(
    (reorderedRigs) => {
      const draftRig = reorderedRigs.find((r) => r.draft);
      const rest = reorderedRigs.filter((r) => !r.draft);
      const final = draftRig ? [draftRig, ...rest] : rest;

      setRigs(final);
      if (apiClient) {
        apiClient.reorderRigs(rest.map((r) => r.id));
      }
    },
    [apiClient, setRigs]
  );

  const handleRenameRig = useCallback(
    (rigId, newName) => {
      rigNameRef.current = newName;
      setRigs((prev) =>
        prev.map((r) => (r.id === rigId ? { ...r, name: newName } : r))
      );
      if (rigId !== DRAFT_RIG_ID) {
        saveNow();
      }
    },
    [rigNameRef, setRigs, saveNow]
  );

  const handleDeleteRig = useCallback(
    async (rigId) => {
      if (!apiClient) return;

      // Deleting the draft tab: local-only cleanup
      if (rigId === DRAFT_RIG_ID) {
        clearDraft();
        const remaining = rigs.filter((r) => !r.draft);
        setRigs(remaining);
        if (currentRigId === DRAFT_RIG_ID) {
          if (remaining.length > 0) {
            handleSwitchRig(remaining[0].id);
          } else {
            setCurrentRigId(null);
            loadState({ nodes: [], edges: [] });
          }
        }
        return;
      }

      try {
        await apiClient.deleteRig(rigId);
        let remaining = rigs.filter((r) => r.id !== rigId);

        // Check if draft tab should promote
        const draftEntry = remaining.find((r) => r.draft);
        const activeCount = remaining.filter((r) => !r.over_limit && !r.draft).length;

        if (draftEntry && activeCount < maxSavedRigs) {
          const draftData = loadDraft();
          const rawName = draftData?.rigName || "Draft";
          const name = rawName === "Draft" ? "My Rig" : rawName;
          try {
            const newRig = await apiClient.createRig(name);

            if (draftData && (draftData.nodes?.length > 0 || draftData.edges?.length > 0)) {
              const payload = boardStateToSyncPayload(
                draftData.nodes,
                draftData.edges,
                name,
                draftData.rigCables
              );
              await apiClient.syncRig(newRig.id, payload);
            }

            remaining = remaining.map((r) => r.draft ? { ...newRig } : r);
            clearDraft();

            if (rigId === currentRigId || currentRigId === DRAFT_RIG_ID) {
              setCurrentRigId(newRig.id);
              rigNameRef.current = newRig.name;
            }
          } catch {
            // Promotion failed — keep draft tab as-is
          }
        }

        setRigs(remaining);

        if (rigId === currentRigId && !remaining.find((r) => r.id === currentRigId)) {
          if (remaining.length > 0) {
            handleSwitchRig(remaining[0].id);
          } else {
            setCurrentRigId(null);
            loadState({ nodes: [], edges: [] });
          }
        }
      } catch {
        // ignore
      }
    },
    [apiClient, rigs, currentRigId, maxSavedRigs, setRigs, setCurrentRigId, rigNameRef, handleSwitchRig, loadState]
  );

  return {
    handleSwitchRig,
    handleNewRig,
    handleDuplicateRig,
    handleTogglePrivate,
    handleUpdateSlug,
    handleReorderRigs,
    handleRenameRig,
    handleDeleteRig,
  };
}
